import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Store, Plus, MapPin, Clock, Search, Tag, Info, X, Edit3, Trash2, ArrowLeft } from 'lucide-react';
import { useAppStore } from '../../shared/store';
import { generateId } from '../../shared/utils/id';
import { toast } from '../../shared/components/Toast';
import { ConfirmDialog } from '../../shared/components/ConfirmDialog';
import { Store as StoreType } from '../../shared/types';
export function StoresPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showInfo, setShowInfo] = useState(true);
  const [storeToDelete, setStoreToDelete] = useState<string | null>(null);
  const [editingStore, setEditingStore] = useState<StoreType | null>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState<StoreType['type']>('supermarket');
  const [editLocation, setEditLocation] = useState('');
  const [editHours, setEditHours] = useState('');
  const {
    stores,
    activeDeals,
    addStore,
    updateStore,
    removeStore
  } = useAppStore();
  const filteredStores = useMemo(() => {
    return stores.filter(store => store.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [stores, searchQuery]);
  const storeDeals = useMemo(() => {
    const counts: Record<string, number> = {};
    activeDeals.forEach(deal => {
      if (deal.isActive && new Date(deal.endDate) > new Date()) {
        counts[deal.retailerId] = (counts[deal.retailerId] || 0) + 1;
      }
    });
    return counts;
  }, [activeDeals]);
  const handleToggleActive = (store: StoreType) => {
    updateStore(store.id, {
      isActive: !store.isActive
    });
    toast.success(`${store.name} is nu ${!store.isActive ? 'actief' : 'inactief'}`);
  };
  const handleDeleteConfirm = () => {
    if (storeToDelete) {
      removeStore(storeToDelete);
      toast.success('Winkel succesvol verwijderd');
      setStoreToDelete(null);
    }
  };
  const handleOpenAddDialog = () => {
    setIsAddMode(true);
    setEditingStore({
      id: '',
      name: '',
      color: '#6366f1',
      type: 'supermarket'
    } as StoreType);
    setEditName('');
    setEditType('supermarket');
    setEditLocation('');
    setEditHours('');
  };
  const handleStartEdit = (store: StoreType) => {
    setIsAddMode(false);
    setEditingStore(store);
    setEditName(store.name);
    setEditType(store.type || 'supermarket');
    setEditLocation(store.location || '');
    setEditHours(store.openingHours || '');
  };
  const handleSaveEdit = () => {
    if (!editName.trim()) {
      toast.error('Vul een naam in');
      return;
    }
    if (isAddMode) {
      addStore({
        id: generateId(),
        name: editName.trim(),
        color: '#6366f1',
        type: editType,
        isActive: true,
        location: editLocation.trim() || undefined,
        openingHours: editHours.trim() || undefined
      });
      toast.success(`${editName} toegevoegd`);
    } else if (editingStore) {
      updateStore(editingStore.id, {
        name: editName.trim(),
        type: editType,
        location: editLocation.trim() || undefined,
        openingHours: editHours.trim() || undefined
      });
      toast.success(`${editName} bijgewerkt`);
    }
    setEditingStore(null);
    setIsAddMode(false);
  };
  return <div className="min-h-screen bg-background pb-24" data-id="element-2192" data-mp="k74vi24km01s">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border" data-id="element-2193" data-mp="fb7hi2gs55s6">
        <div className="p-6" data-id="element-2194" data-mp="u03xclp2vn3c">
          <div className="flex items-center gap-3 mb-4" data-id="element-2195" data-mp="u0dinvz6h62p">
            <button onClick={() => navigate('/profile')} className="p-2 rounded-lg bg-card text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" data-id="element-2196" data-mp="p82iqt4ftka5">
              <ArrowLeft className="w-5 h-5" data-id="element-2197" data-mp="3so9iiojqexdn" />
            </button>
            <div data-id="element-2198" data-mp="2vy7fdhk3sul6">
              <h1 className="text-2xl font-bold text-foreground" data-id="element-2199" data-mp="2vyp8447k4qoc">Winkels</h1>
              <p className="mt-0.5 text-sm text-muted-foreground" data-id="element-2200" data-mp="2zyzma45i6nea">Activeer winkels waar je vaak komt voor passende aanbiedingen.</p>
            </div>
          </div>
          <div className="relative" data-id="element-2201" data-mp="34w9ajgh5ot0j">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" data-id="element-2202" data-mp="39plzjrpqz1tt" />
            <input type="text" placeholder="Zoek winkels..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3.5 bg-card border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground placeholder-muted-foreground" data-id="element-2203" data-mp="3eoow0nrnvzq6" />
          </div>
        </div>
      </div>

      <div className="px-6 pt-4 space-y-5" data-id="element-2204" data-mp="1tbm3iru0vwbu">
        <AnimatePresence data-id="element-2205" data-mp="1tcx64obqey1d">
          {showInfo && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="flex items-start justify-between gap-3 p-4 bg-primary/10 rounded-xl border border-primary/20 overflow-hidden" data-id="element-2206" data-mp="1y943vttz5388">
              <div className="flex items-start gap-3" data-id="element-2207" data-mp="29p8naf7go983">
                <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" data-id="element-2208" data-mp="29oqkt0u7gyrq" />
                <p className="text-sm text-primary leading-relaxed" data-id="element-2209" data-mp="24r76k0xiclzs">
                  Beheer je winkels en activeer alleen de winkels waar je
                  regelmatig komt voor betere aanbevelingen.
                </p>
              </div>
              <button onClick={() => setShowInfo(false)} className="p-1.5 hover:bg-primary/20 rounded-lg transition-colors flex-shrink-0" data-id="element-2210" data-mp="1vrk3lap4r84m">
                <X className="w-4 h-4 text-primary" data-id="element-2211" data-mp="k90y4xcfayd1" />
              </button>
            </motion.div>}
        </AnimatePresence>

        {filteredStores.length === 0 ? <div className="text-center py-12 bg-card border border-border rounded-xl" data-id="element-2212" data-mp="qp9ilxw2l347">
            <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-accent flex items-center justify-center" data-id="element-2213" data-mp="bsa21kj8pzxl">
              <Store className="w-8 h-8 text-muted-foreground" data-id="element-2214" data-mp="gr3f5bum7ox8" />
            </div>
            <p className="mb-1 font-semibold text-foreground" data-id="element-2215" data-mp="30q6f79wg66u">
              Voeg je favoriete winkels toe
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground" data-id="element-2216" data-mp="7shyjm8pejz3">
              Dan zie je aanbiedingen die passen bij waar jij boodschappen doet.
            </p>
          </div> : <div className="space-y-4" data-id="element-2217" data-mp="3u7szk49asmjp">
            <AnimatePresence data-id="element-2218" data-mp="3g7i7ixw951wy">
              {filteredStores.map(store => <motion.div layout initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} key={store.id} className={`bg-card border rounded-2xl overflow-hidden transition-all ${store.isActive ? 'border-border' : 'border-border/40 bg-card/50 opacity-70'}`} data-id="element-2219" data-mp="3g9mly9rs1jbh">
                  <div className="p-5" data-id="element-2220" data-mp="3khcdnbdlhuid">
                    <div className="flex items-start gap-4 mb-4" data-id="element-2221" data-mp="3fldsdwnkcsm6">
                      <div className={`p-3 rounded-xl ${store.isActive ? 'bg-primary/10' : 'bg-accent'}`} data-id="element-2222" data-mp="3tbzctpso1am6">
                        <Store className={`w-5 h-5 ${store.isActive ? 'text-primary' : 'text-muted-foreground'}`} data-id="element-2223" data-mp="3paxdl8yr0yk7" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2224" data-mp="270n79n97r92">
                        <div className="flex items-center justify-between gap-2" data-id="element-2225" data-mp="19if07hwgpief">
                          <div className="min-w-0" data-id="element-2226" data-mp="19khmbquhurdv">
                            <h3 className="font-medium text-foreground truncate" data-id="element-2227" data-mp="1dife91fnfrky">
                              {store.name}
                            </h3>
                            {store.type && <p className="text-sm text-muted-foreground mt-0.5 capitalize" data-id="element-2228" data-mp="1nglhbmd6z3re">
                                {store.type}
                              </p>}
                          </div>
                          <button onClick={() => handleToggleActive(store)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex-shrink-0 ${store.isActive ? 'bg-success/10 text-success border border-success/20' : 'bg-accent text-muted-foreground border border-border'}`} data-id="element-2229" data-mp="uqwcexxd2jki">
                            {store.isActive ? 'Actief' : 'Inactief'}
                          </button>
                        </div>
                        <div className="mt-4 space-y-2" data-id="element-2230" data-mp="3vpjhcpqkytrs">
                          {store.location && <div className="flex items-center gap-2 text-sm text-muted-foreground" data-id="element-2231" data-mp="3qq6n3jxdqkf6">
                              <MapPin className="w-4 h-4" data-id="element-2232" data-mp="3lrlua5kgj30r" />
                              <span className="truncate" data-id="element-2233" data-mp="3lr3tcvmuo0mi">{store.location}</span>
                            </div>}
                          {store.openingHours && <div className="flex items-center gap-2 text-sm text-muted-foreground" data-id="element-2234" data-mp="3cxqpwzr5tgm6">
                              <Clock className="w-4 h-4" data-id="element-2235" data-mp="3808ft61eovcl" />
                              <span data-id="element-2236" data-mp="331ndn12lg1li">{store.openingHours}</span>
                            </div>}
                        </div>
                      </div>
                    </div>
                    {storeDeals[store.id] > 0 && <div className="flex items-center gap-2 px-3 py-2 bg-success/10 rounded-xl border border-success/20 text-success" data-id="element-2237" data-mp="2u2jszsns3e4w">
                        <Tag className="w-4 h-4" data-id="element-2238" data-mp="353q3tyd9z47b" />
                        <span className="text-sm font-medium" data-id="element-2239" data-mp="350u6ab5cahje">
                          {storeDeals[store.id]} actieve{' '}
                          {storeDeals[store.id] === 1 ? 'deal' : 'deals'}
                        </span>
                      </div>}
                  </div>
                  <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2 bg-accent/30" data-id="element-2240" data-mp="2r59ndfpm1zbz">
                    <button onClick={() => handleStartEdit(store)} className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors" data-id="element-2241" data-mp="303vehz95emod">
                      <Edit3 className="w-4 h-4" data-id="element-2242" data-mp="fcl5vytyflyv" />
                    </button>
                    <button onClick={() => setStoreToDelete(store.id)} className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors" data-id="element-2243" data-mp="bckg5i1enoci">
                      <Trash2 className="w-4 h-4" data-id="element-2244" data-mp="nvohzj9c4koq" />
                    </button>
                  </div>
                </motion.div>)}
            </AnimatePresence>
          </div>}
      </div>

      <div className="fixed bottom-24 right-6 z-20" data-id="element-2245" data-mp="dwz74afuw0hz">
        <button onClick={handleOpenAddDialog} className="w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg shadow-primary/30 hover:scale-105 transition-transform flex items-center justify-center" aria-label="Winkel toevoegen" data-id="element-2246" data-mp="11mbgbab6o8ka">
          <Plus className="w-6 h-6" data-id="element-2247" data-mp="wvbim2u8pmgq" />
        </button>
      </div>

      <ConfirmDialog isOpen={!!storeToDelete} title="Winkel verwijderen" description="Weet je zeker dat je deze winkel wilt verwijderen? Dit kan niet ongedaan worden gemaakt." confirmText="Verwijderen" cancelText="Annuleren" variant="danger" onConfirm={handleDeleteConfirm} onCancel={() => setStoreToDelete(null)} data-id="element-2248" data-mp="wti9mgw5mq5k" />

      {/* Edit Store Dialog */}
      <AnimatePresence data-id="element-2249" data-mp="1u6ox6a0anaqz">
        {editingStore && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => {
        setEditingStore(null);
        setIsAddMode(false);
      }} data-id="element-2250" data-mp="1q5vafjyl81zo">
            <motion.div initial={{
          y: 100,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 100,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} onClick={e => e.stopPropagation()} className="w-full sm:max-w-md bg-card border border-border rounded-t-2xl sm:rounded-2xl p-6" data-id="element-2251" data-mp="12hc3p8v6rx9p">
              <div className="flex items-center justify-between mb-6" data-id="element-2252" data-mp="3i6sokz36s7tj">
                <div className="flex items-center gap-3" data-id="element-2253" data-mp="3i3y5bavwst1p">
                  <div className="p-2 bg-primary/10 rounded-xl" data-id="element-2254" data-mp="2lhb8rrahp5e4">
                    {isAddMode ? <Store className="w-5 h-5 text-primary" data-id="element-2255" data-mp="2qfwci0oya77b" /> : <Edit3 className="w-5 h-5 text-primary" data-id="element-2256" data-mp="2ufn6ed9rx3g2" />}
                  </div>
                  <h2 className="text-lg font-semibold text-foreground" data-id="element-2257" data-mp="2z8807esojgjl">
                    {isAddMode ? 'Winkel Toevoegen' : 'Winkel Bewerken'}
                  </h2>
                </div>
                <button onClick={() => {
              setEditingStore(null);
              setIsAddMode(false);
            }} className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors" data-id="element-2258" data-mp="26q2g19baj3wr">
                  <X className="w-5 h-5" data-id="element-2259" data-mp="1xd3nhlkk7g7x" />
                </button>
              </div>

              <div className="space-y-4" data-id="element-2260" data-mp="1skk9inhymj2l">
                <div className="space-y-1.5" data-id="element-2261" data-mp="1sjimrlrl0851">
                  <label className="text-sm font-medium text-foreground" data-id="element-2262" data-mp="26c77crrjvznh">
                    Naam
                  </label>
                  <input type="text" value={editName} onChange={e => setEditName(e.target.value)} className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-foreground" data-id="element-2263" data-mp="21d42b7bkltfi" />
                </div>
                <div className="space-y-1.5" data-id="element-2264" data-mp="gylnf2vxks7h">
                  <label className="text-sm font-medium text-foreground" data-id="element-2265" data-mp="gzwm4oair9rq">
                    Type
                  </label>
                  <select value={editType} onChange={e => setEditType(e.target.value as StoreType['type'])} className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground" data-id="element-2266" data-mp="2xkigfy8duxv8">
                    <option value="supermarket" data-id="element-2267" data-mp="29tcqgq8qap77">Supermarkt</option>
                    <option value="wholesale" data-id="element-2268" data-mp="2dx115hsnq0wo">Groothandel</option>
                    <option value="discount" data-id="element-2269" data-mp="2du6gbp5qee2q">Discount</option>
                    <option value="specialty" data-id="element-2270" data-mp="1zy1iqrvqmi8y">Speciaalzaak</option>
                    <option value="other" data-id="element-2271" data-mp="24vb867j4mtws">Overig</option>
                  </select>
                </div>
                <div className="space-y-1.5" data-id="element-2272" data-mp="3uaho7qgplscr">
                  <label className="text-sm font-medium text-foreground" data-id="element-2273" data-mp="3u8oge7ud1236">
                    Locatie
                  </label>
                  <input type="text" value={editLocation} onChange={e => setEditLocation(e.target.value)} placeholder="bijv. Amsterdam" className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-foreground placeholder-muted-foreground" data-id="element-2274" data-mp="1nasnne80a12" />
                </div>
                <div className="space-y-1.5" data-id="element-2275" data-mp="kd95egsyqrb1">
                  <label className="text-sm font-medium text-foreground" data-id="element-2276" data-mp="fez8o1rlbinb">
                    Openingstijden
                  </label>
                  <input type="text" value={editHours} onChange={e => setEditHours(e.target.value)} placeholder="bijv. 08:00 - 22:00" className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-foreground placeholder-muted-foreground" data-id="element-2277" data-mp="amo7kvl45qt6" />
                </div>
              </div>

              <div className="flex gap-3 mt-6" data-id="element-2278" data-mp="1an9j2x6lnq7m">
                <button onClick={() => {
              setEditingStore(null);
              setIsAddMode(false);
            }} className="flex-1 py-3 bg-accent text-foreground rounded-xl font-medium hover:bg-accent/80 transition-colors" data-id="element-2279" data-mp="1ao2re2l7inrv">
                  Annuleren
                </button>
                <button onClick={handleSaveEdit} className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:opacity-90 transition-opacity" data-id="element-2280" data-mp="1tlvje9ualdah">
                  {isAddMode ? 'Toevoegen' : 'Opslaan'}
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}